// src/features/assessment/engine/logic.ts

import { TrackId, Department } from './types';

export interface LogicOutput {
  primaryResultId: string;
  context: { 
    department: Department | 'default';
  };
  relatedResultIds: string[]; // For the "Explore Alternatives" section
}

export const calculateAssessmentResult = (track: TrackId, answers: Record<string, string>): LogicOutput => {
  
  // ==========================================
  // TRACK: CREATOR
  // ==========================================
  if (track === 'creator') {
    // 1. Bootstrapper (High Priority Fail State)
    if (answers['q1_leverage'] === 'c' || answers['q3_capital'] === 'a') {
      return { 
        primaryResultId: 'bootstrapper', 
        context: { department: 'default' }, 
        relatedResultIds: ['sovereign_founder'] // Show them the goal
      };
    }

    // 2. Traditional Builder (Mindset Risk)
    // q1=a (Big team) OR q2=a (Custom scratch build) OR q2=c (SaaS renter)
    if (answers['q1_leverage'] === 'a' || answers['q2_infrastructure'] === 'a' || answers['q2_infrastructure'] === 'c') {
      return { 
        primaryResultId: 'traditional_builder', 
        context: { department: 'default' }, 
        relatedResultIds: ['sovereign_founder'] // Show them the better way
      };
    }

    // 3. Sovereign Founder (Ideal)
    return { 
      primaryResultId: 'sovereign_founder', 
      context: { department: 'default' }, 
      relatedResultIds: ['traditional_builder'] // Show them the trap they avoided
    };
  }

  // ==========================================
  // TRACK: TRANSFORMER
  // ==========================================
  if (track === 'transformer') {
    // Extract Department (default to engineering if missing)
    const dept = (answers['q2_priority'] || 'engineering') as Department;

    // 1. Digital Transition (Analog/Offline)
    // Offline decisions OR No Docs OR Zero Tolerance
    if (answers['q1_signal'] === 'a' || answers['q3_memory'] === 'a' || answers['q4_governance'] === 'a') {
      return { 
        primaryResultId: 'transformer_digital', 
        context: { department: dept }, 
        relatedResultIds: ['transformer_cleanup'] // The next step up
      };
    }

    // 2. Clean-Up Phase (Messy Middle)
    // Digital but scattered OR Old Docs
    if (answers['q1_signal'] === 'b' || answers['q3_memory'] === 'b') {
      return { 
        primaryResultId: 'transformer_cleanup', 
        context: { department: dept }, 
        relatedResultIds: ['transformer_architect', 'transformer_digital'] // Show path forward and backward
      };
    }

    // 3. The Architect (Ready)
    return { 
      primaryResultId: 'transformer_architect', 
      context: { department: dept }, 
      relatedResultIds: ['transformer_cleanup'] 
    };
  }

  // ==========================================
  // TRACK: BUILDER
  // ==========================================
  if (track === 'builder') {
    const isNewApp = answers['q1_context'] === 'new';
    
    if (isNewApp) {
      // 1. Wrapper Risk
      if (answers['q2b_core_value'] === 'a') {
        return { 
          primaryResultId: 'wrapper_warning', 
          context: { department: 'default' }, 
          relatedResultIds: ['ai_native_founder'] 
        };
      }
      // 2. AI Native
      return { 
        primaryResultId: 'ai_native_founder', 
        context: { department: 'default' }, 
        relatedResultIds: ['wrapper_warning'] 
      };
    } else {
      // Existing App
      // 3. Feature Injection (Read Only)
      if (answers['q2a_integration'] === 'c') {
        return { 
          primaryResultId: 'feature_injection', 
          context: { department: 'default' }, 
          relatedResultIds: ['in_app_concierge'] 
        };
      }
      // 4. In-App Concierge (Action)
      return { 
        primaryResultId: 'in_app_concierge', 
        context: { department: 'default' }, 
        relatedResultIds: ['feature_injection'] 
      };
    }
  }

  // Fallback (Should not happen)
  return { 
    primaryResultId: 'sovereign_founder', 
    context: { department: 'default' }, 
    relatedResultIds: [] 
  };
};